import React, { useState, useEffect } from 'react'
import { Icon } from './Icon'
import { Avatar, Button, StatusBadge } from './Primitives'
import { GlobalSearch } from './GlobalSearch'
import { useIsMobile } from '../lib/useIsMobile'
import { useAuth } from '../lib/auth'
import { useApi } from '../lib/useApi'
import logoWordmark from '../assets/logo-wordmark.svg'

type NavKey = 'dashboard' | 'jobs' | 'quotes' | 'invoices' | 'inbox' | 'customers' | 'team' | 'settings' | 'bays' | 'vendors' | 'purchase-orders' | 'reports' | 'help'

interface NavItem {
  key: NavKey
  label: string
  icon: string
  badge?: number
  roles?: string[] // undefined = all roles
}

const NAV: NavItem[] = [
  { key: 'dashboard', label: 'Dashboard', icon: 'gauge' },
  { key: 'jobs',      label: 'Job cards', icon: 'clipboard' },
  { key: 'quotes',    label: 'Quotes',    icon: 'file-text',  roles: ['Owner', 'OfficeManager', 'Foreman'] },
  { key: 'invoices',  label: 'Invoices',  icon: 'receipt',    roles: ['Owner', 'OfficeManager'] },
  { key: 'inbox',     label: 'WhatsApp',  icon: 'wa',         roles: ['Owner', 'OfficeManager'] },
  { key: 'customers', label: 'Customers', icon: 'users',      roles: ['Owner', 'OfficeManager', 'Foreman'] },
  { key: 'team',           label: 'Team',            icon: 'user-cog',     roles: ['Owner', 'OfficeManager', 'Foreman'] },
  { key: 'bays',           label: 'Bay board',       icon: 'layout-grid',  roles: ['Owner', 'OfficeManager', 'Foreman', 'Mechanic'] },
  { key: 'vendors',        label: 'Vendors',         icon: 'truck',        roles: ['Owner', 'OfficeManager', 'Foreman'] },
  { key: 'purchase-orders', label: 'Purchase orders', icon: 'shopping-cart', roles: ['Owner', 'OfficeManager', 'Foreman', 'Mechanic'] },
  { key: 'reports',         label: 'Reports',         icon: 'bar-chart-2',  roles: ['Owner', 'OfficeManager', 'Foreman'] },
  { key: 'help',             label: 'Help',             icon: 'help-circle' },
]

interface ShellProps {
  active: NavKey
  onNav: (key: NavKey) => void
  children: React.ReactNode
  offline?: boolean
  onNewJob?: () => void
  onSettings?: () => void
  onLogout?: () => void
  onSearch?: (query: string) => void
  inboxBadge?: number
}

type TenantNavState = {
  subscriptionStatus: string
  subscriptionTier: string
  cancellationEffectiveAt: string | null
}

function NavRail({
  active, onNav, onSettings, onLogout, inboxBadge, onClose, role, userName,
}: Pick<ShellProps, 'active' | 'onNav' | 'onSettings' | 'onLogout' | 'inboxBadge'> & { onClose?: () => void; role: string; userName: string }) {
  const visibleNav = NAV.filter(n => !n.roles || n.roles.includes(role))
  return (
    <aside style={{
      width: 240, background: '#fff',
      borderRight: '1px solid var(--border-subtle)',
      display: 'flex', flexDirection: 'column', flexShrink: 0,
      height: '100%',
    }}>
      <div style={{
        padding: '18px 18px 16px',
        borderBottom: '1px solid var(--border-subtle)',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <img src={logoWordmark} width="140" alt="AutoAxxiz" style={{ display: 'block' }} />
        {onClose && (
          <button
            onClick={onClose}
            aria-label="Close menu"
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              padding: 4, borderRadius: 6, color: 'var(--fg-3)', display: 'flex',
            }}
          >
            <Icon name="x" size={18} />
          </button>
        )}
      </div>

      <div style={{ padding: '10px 10px', display: 'flex', flexDirection: 'column', gap: 2, flex: 1 }}>
        {visibleNav.map(n => {
          const isActive = n.key === active
          return (
            <button
              key={n.key}
              onClick={() => { onNav(n.key); onClose?.() }}
              style={{
                display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px',
                background: isActive ? 'var(--navy-50)' : 'transparent',
                color: isActive ? 'var(--navy-700)' : 'var(--fg-2)',
                border: 'none', borderRadius: 8, cursor: 'pointer',
                fontSize: 14, fontWeight: isActive ? 600 : 500,
                fontFamily: 'inherit', textAlign: 'left', width: '100%',
              }}
            >
              <Icon name={n.icon} size={18} />
              <span style={{ flex: 1 }}>{n.label}</span>
              {n.key === 'inbox' && inboxBadge != null && inboxBadge > 0 && (
                <span style={{
                  background: 'var(--orange-500)', color: '#fff',
                  fontSize: 11, fontWeight: 700, padding: '2px 6px',
                  borderRadius: 999, minWidth: 18, textAlign: 'center',
                }}>{inboxBadge}</span>
              )}
            </button>
          )
        })}
      </div>

      <div style={{ padding: 14, borderTop: '1px solid var(--border-subtle)', display: 'flex', alignItems: 'center', gap: 10 }}>
        <Avatar name={userName} size={32} color="var(--orange-500)" />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--fg-1)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{userName}</div>
          <div style={{ fontSize: 11, color: 'var(--fg-3)' }}>{role}</div>
        </div>
        <button onClick={onSettings} title="Settings" style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, borderRadius: 6, color: active === 'settings' ? 'var(--navy-700)' : 'var(--fg-3)' }}>
          <Icon name="settings" size={16} />
        </button>
        <button onClick={onLogout} title="Log out" style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, borderRadius: 6, color: 'var(--fg-3)' }}>
          <Icon name="log-out" size={16} />
        </button>
      </div>
    </aside>
  )
}

export function Shell({ active, onNav, children, offline = false, onNewJob, onSettings, onLogout, onSearch, inboxBadge }: ShellProps) {
  const isMobile = useIsMobile()
  const [drawerOpen, setDrawerOpen] = useState(false)
  const { user } = useAuth()
  const api = useApi()
  const role = user?.role ?? ''
  const [userName, setUserName] = useState('')
  const [tenant, setTenant] = useState<TenantNavState | null>(null)

  useEffect(() => {
    if (!user) return
    api.get<{ name: string }>('/v1/users/me').then(u => setUserName(u.name)).catch(() => {})
  }, [user]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!user) return
    api.get<TenantNavState>('/v1/tenants/me').then(setTenant).catch(() => {})
  }, [user]) // eslint-disable-line react-hooks/exhaustive-deps

  const bottomTabs: Array<{ key: NavKey | '__more__'; label: string; icon: string; badge?: boolean }> = (() => {
    const tabs: Array<{ key: NavKey | '__more__'; label: string; icon: string; badge?: boolean }> = [
      { key: 'dashboard', label: 'Home', icon: 'gauge' },
      { key: 'jobs',      label: 'Jobs', icon: 'clipboard' },
    ]
    if (role === 'Owner' || role === 'OfficeManager') tabs.push({ key: 'inbox', label: 'WhatsApp', icon: 'wa', badge: true })
    if (role !== 'Mechanic') tabs.push({ key: 'customers', label: 'Customers', icon: 'users' })
    tabs.push({ key: '__more__', label: 'More', icon: 'menu' })
    return tabs
  })()

  return (
    <div style={{ display: 'flex', height: '100vh', background: 'var(--bg-2)', fontFamily: 'var(--font-sans)' }}>

      {/* ── Desktop sidebar ─────────────────────────────────────── */}
      {!isMobile && (
        <NavRail
          active={active} onNav={onNav}
          onSettings={onSettings} onLogout={onLogout} inboxBadge={inboxBadge}
          role={role} userName={userName}
        />
      )}

      {/* ── Mobile drawer overlay ────────────────────────────────── */}
      {isMobile && drawerOpen && (
        <>
          {/* Scrim */}
          <div
            onClick={() => setDrawerOpen(false)}
            style={{
              position: 'fixed', inset: 0, zIndex: 40,
              background: 'rgba(0,0,0,0.4)',
            }}
          />
          {/* Drawer */}
          <div style={{
            position: 'fixed', top: 0, left: 0, bottom: 0, zIndex: 50,
            width: 260, display: 'flex', flexDirection: 'column',
            boxShadow: 'var(--shadow-xl)',
          }}>
            <NavRail
              active={active} onNav={onNav}
              onSettings={onSettings} onLogout={onLogout}
              inboxBadge={inboxBadge} onClose={() => setDrawerOpen(false)}
              role={role} userName={userName}
            />
          </div>
        </>
      )}

      {/* ── Main area ────────────────────────────────────────────── */}
      <main style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        {/* Top bar */}
        <header style={{
          height: 56, borderBottom: '1px solid var(--border-subtle)',
          background: '#fff', display: 'flex', alignItems: 'center',
          gap: isMobile ? 8 : 14, padding: `0 ${isMobile ? 16 : 24}px`, flexShrink: 0,
        }}>
          {/* Hamburger on mobile */}
          {isMobile && (
            <button
              onClick={() => setDrawerOpen(true)}
              aria-label="Open menu"
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                padding: 4, borderRadius: 6, color: 'var(--fg-2)', display: 'flex', flexShrink: 0,
              }}
            >
              <Icon name="menu" size={20} />
            </button>
          )}

          <div style={{ flex: 1, minWidth: 0 }}>
            <GlobalSearch onNavigateJobs={onSearch ?? (() => {})} />
          </div>

          {tenant && (
            <TenantStatusNav tenant={tenant} isOwner={role === 'Owner'} compact={isMobile} />
          )}

          {!isMobile && (
            offline ? (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 999, background: '#FEF3F2', color: '#B42318', fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap' }}>
                <span style={{ width: 6, height: 6, borderRadius: 999, background: '#F04438' }} /> Offline · 3 queued
              </span>
            ) : (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 999, background: '#ECFDF3', color: '#027A48', fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap' }}>
                <span style={{ width: 6, height: 6, borderRadius: 999, background: '#12B76A' }} /> All synced
              </span>
            )
          )}

          <button
            onClick={() => onNav('inbox')}
            title="WhatsApp inbox"
            style={{ position: 'relative', background: 'none', border: 'none', cursor: 'pointer', padding: 4, borderRadius: 6, color: 'var(--fg-2)', display: 'flex', flexShrink: 0 }}
          >
            <Icon name="bell" size={18} />
            {inboxBadge != null && inboxBadge > 0 && (
              <span style={{
                position: 'absolute', top: 0, right: 0,
                width: 8, height: 8, borderRadius: 999,
                background: 'var(--orange-500)', border: '2px solid #fff',
              }} />
            )}
          </button>

          {isMobile ? (
            <button
              onClick={onNewJob}
              title="New job card"
              style={{
                background: 'var(--navy-700)', border: 'none', cursor: 'pointer',
                borderRadius: 8, padding: 8, color: '#fff', display: 'flex', flexShrink: 0,
              }}
            >
              <Icon name="plus" size={18} />
            </button>
          ) : (
            <Button variant="primary" icon="plus" onClick={onNewJob}>New job card</Button>
          )}
        </header>

        <div style={{ flex: 1, overflow: 'auto', paddingBottom: isMobile ? 64 : 0 }}>{children}</div>
      </main>

      {/* ── Mobile bottom nav ────────────────────────────────────── */}
      {isMobile && (
        <nav style={{
          position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 30,
          height: 60, background: '#fff', borderTop: '1px solid var(--border-subtle)',
          display: 'flex', alignItems: 'stretch',
          paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        }}>
          {bottomTabs.map(tab => {
            const isActive = tab.key !== '__more__' && active === tab.key
            return (
              <button
                key={tab.key}
                onClick={() => tab.key === '__more__' ? setDrawerOpen(true) : onNav(tab.key as NavKey)}
                style={{
                  flex: 1, display: 'flex', flexDirection: 'column',
                  alignItems: 'center', justifyContent: 'center', gap: 3,
                  background: 'none', border: 'none', cursor: 'pointer', padding: '6px 4px',
                  color: isActive ? 'var(--navy-700)' : 'var(--fg-3)',
                  fontFamily: 'inherit',
                }}
              >
                <div style={{ position: 'relative' }}>
                  <Icon name={tab.icon} size={20} />
                  {tab.badge && inboxBadge != null && inboxBadge > 0 && (
                    <span style={{
                      position: 'absolute', top: -2, right: -6,
                      width: 8, height: 8, borderRadius: 999,
                      background: 'var(--orange-500)', border: '2px solid #fff',
                    }} />
                  )}
                </div>
                <span style={{ fontSize: 10, fontWeight: isActive ? 700 : 500, lineHeight: 1 }}>{tab.label}</span>
              </button>
            )
          })}
        </nav>
      )}
    </div>
  )
}

function TenantStatusNav({ tenant, isOwner, compact }: { tenant: TenantNavState; isOwner: boolean; compact: boolean }) {
  const status = tenantStatusLabel(tenant.subscriptionStatus)
  const cancelled = tenant.subscriptionStatus === 'Cancelled'
  const effective = tenant.cancellationEffectiveAt ? formatDate(tenant.cancellationEffectiveAt) : null

  return (
    <div style={{
      display: 'inline-flex', alignItems: 'center', gap: 8, minWidth: 0,
      maxWidth: compact ? 118 : 320, flexShrink: 0,
    }}>
      <StatusBadge status={status} />
      {!compact && isOwner && cancelled && effective && (
        <span style={{
          fontSize: 12, color: 'var(--fg-3)', whiteSpace: 'nowrap',
          overflow: 'hidden', textOverflow: 'ellipsis',
        }}>
          Effective {effective}
        </span>
      )}
    </div>
  )
}

function tenantStatusLabel(status: string): string {
  switch (status) {
    case 'PastDue': return 'PAST DUE'
    case 'MultiSite': return 'MULTI SITE'
    default: return status.replace(/([a-z])([A-Z])/g, '$1 $2').toUpperCase()
  }
}

function formatDate(value: string): string {
  return new Intl.DateTimeFormat('en-ZA', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(new Date(value))
}
